import { keyframes } from '@emotion/react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { RunnerResultCard } from '@/core/components';
import type { Runner } from '@/core/types';
import { calcNetTime, getFinishTime, getStartTime } from '@/core/types';

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  }
  50% {
    box-shadow: 0 4px 20px rgba(255, 136, 0, 0.4);
  }
`;

// トロフィーのバウンスアニメーション
const bounce = keyframes`
  0% {
    transform: scale(0) rotate(-10deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(5deg);
  }
  70% {
    transform: scale(0.9) rotate(-3deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
`;

// トロフィーのシャインエフェクト
const shine = keyframes`
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

interface ResultConfirmStepProps {
  runner: Runner;
  isAlreadyIssued: boolean;
  onClaim: (destination?: string) => Promise<void>;
}

export function ResultConfirmStep({ runner, isAlreadyIssued, onClaim }: ResultConfirmStepProps) {
  const theme = useTheme();
  const gradient = theme.custom?.gradient || theme.palette.primary.main;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finishTime = getFinishTime(runner);
  const netTime = calcNetTime(getStartTime(runner), finishTime);

  const handleClaim = async () => {
    setLoading(true);
    setError(null);

    try {
      await onClaim();
    } catch (err) {
      setError('エラーが発生しました。もう一度お試しください');
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* 祝福メッセージ */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        {/* トロフィー + シャインエフェクト */}
        <Box
          sx={{
            display: 'inline-block',
            position: 'relative',
            animation: `${bounce} 0.6s ease-out`,
          }}
        >
          <EmojiEventsIcon
            sx={{
              fontSize: 48,
              color: theme.palette.warning.main,
              mb: 1,
            }}
          />
          {/* シャインオーバーレイ */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: `${shine} 2s ease-in-out 0.6s infinite`,
              pointerEvents: 'none',
            }}
          />
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            background: gradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          完走おめでとうございます！
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 結果カード - グラデーションボーダー */}
      <Box sx={{ mb: 4 }}>
        <RunnerResultCard bib={runner.bib} grossTime={finishTime} netTime={netTime} />
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={handleClaim}
        disabled={loading || isAlreadyIssued}
        sx={{
          background: gradient,
          color: '#fff',
          height: 52,
          fontWeight: 600,
          fontSize: '1rem',
          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          animation: loading || isAlreadyIssued ? 'none' : `${pulse} 2s ease-in-out infinite`,
          '&:hover': {
            background: gradient,
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
            animation: 'none',
          },
          '&:disabled': {
            background: theme.palette.action.disabledBackground,
            animation: 'none',
          },
        }}
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : isAlreadyIssued ? (
          '発行済みです'
        ) : (
          'デジタル完走証を受け取る'
        )}
      </Button>
    </Box>
  );
}
